
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SchemaOrg from '../../components/feature/SchemaOrg';

interface PriceRange {
  studio: string;
  twoRoom: string;
  threeRoom: string;
  office: string;
}

interface RegionPrice {
  region: string;
  city: string;
  difficulty: string;
  prices: PriceRange;
  extraFees: string[];
  notes: string;
  popular: boolean;
}

const PriceComparison = () => {
  const [selectedType, setSelectedType] = useState<'studio' | 'twoRoom' | 'threeRoom' | 'office'>('studio');
  const [sortBy, setSortBy] = useState<'region' | 'price'>('region');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const regionPrices: RegionPrice[] = [
    {
      region: '台北市',
      city: '大安區、信義區、中山區',
      difficulty: '中高',
      prices: {
        studio: '3,500-5,000',
        twoRoom: '6,000-9,000',
        threeRoom: '9,000-15,000',
        office: '15,000-30,000'
      },
      extraFees: ['老公寓樓層費 +500-1,000/層', '停車證申請', '豪宅管理費'],
      notes: '市中心停車困難，建議提前申請停車證。老公寓無電梯需加收樓層費。',
      popular: true
    },
    {
      region: '新北市',
      city: '板橋、新莊、中和、永和',
      difficulty: '中',
      prices: {
        studio: '3,000-4,500',
        twoRoom: '5,500-8,000',
        threeRoom: '8,000-13,000',
        office: '13,000-25,000'
      },
      extraFees: ['山區加價 +1,000-2,000', '跨區費用', '樓層費 +300-800/層'],
      notes: '板橋、新莊交通便利價格適中。淡水、林口、三峽等偏遠地區需加價。',
      popular: true
    },
    {
      region: '桃園市',
      city: '桃園區、中壢區、平鎮區',
      difficulty: '中低',
      prices: {
        studio: '2,800-4,000',
        twoRoom: '5,000-7,500',
        threeRoom: '7,500-12,000',
        office: '12,000-22,000'
      },
      extraFees: ['工業區搬遷 +2,000-5,000', '跨縣市費用', '樓層費 +300-600/層'],
      notes: '工業區搬遷需提前預約。跨縣市搬家（桃園↔台北）約 +2,000-3,000元。',
      popular: true
    },
    {
      region: '新竹市',
      city: '東區、北區、竹北',
      difficulty: '中',
      prices: {
        studio: '3,000-4,500',
        twoRoom: '5,500-8,000',
        threeRoom: '8,000-13,000',
        office: '13,000-25,000'
      },
      extraFees: ['科學園區 +1,000-2,000', '精密設備搬運另計', '樓層費 +300-800/層'],
      notes: '科學園區搬家需配合門禁管制。精密設備搬運需專業包裝，費用另計。',
      popular: false
    },
    {
      region: '台中市',
      city: '西屯區、北屯區、南屯區',
      difficulty: '中',
      prices: {
        studio: '2,800-4,200',
        twoRoom: '5,200-7,800',
        threeRoom: '7,800-12,500',
        office: '12,500-24,000'
      },
      extraFees: ['山區加價 +1,000-2,000', '跨區費用', '樓層費 +300-700/層'],
      notes: '市區交通便利，價格合理。大坑、太平山區需加收山區費用。',
      popular: true
    },
    {
      region: '台南市',
      city: '東區、北區、安平區',
      difficulty: '低',
      prices: {
        studio: '2,500-3,800',
        twoRoom: '4,800-7,200',
        threeRoom: '7,200-11,500',
        office: '11,500-22,000'
      },
      extraFees: ['古蹟區搬運 +500-1,000', '跨區費用', '樓層費 +300-600/層'],
      notes: '市區道路寬敞，搬家相對容易。古蹟區需注意文化資產保護。',
      popular: false
    },
    {
      region: '高雄市',
      city: '三民區、左營區、鳳山區',
      difficulty: '低',
      prices: {
        studio: '2,500-3,800',
        twoRoom: '4,800-7,200',
        threeRoom: '7,200-11,500',
        office: '11,500-22,000'
      },
      extraFees: ['山區加價 +1,000-2,000', '跨區費用', '樓層費 +300-600/層'],
      notes: '市區搬家便利，價格實惠。旗山、美濃等山區需加收費用。',
      popular: true
    },
    {
      region: '基隆市',
      city: '仁愛區、信義區、中正區',
      difficulty: '中高',
      prices: {
        studio: '3,200-4,500',
        twoRoom: '5,500-8,000',
        threeRoom: '8,000-13,000',
        office: '13,000-24,000'
      },
      extraFees: ['山坡地 +1,000-2,000', '老舊社區 +500-1,000', '樓層費 +400-800/層'],
      notes: '多山坡地形，搬家難度較高。老舊社區巷道狹窄，需小型車輛。',
      popular: false
    },
    {
      region: '宜蘭縣',
      city: '宜蘭市、羅東鎮、礁溪鄉',
      difficulty: '中',
      prices: {
        studio: '3,000-4,500',
        twoRoom: '5,500-8,000',
        threeRoom: '8,000-13,000',
        office: '13,000-25,000'
      },
      extraFees: ['跨縣市 +2,000-4,000', '山區加價 +1,000-2,000', '樓層費 +300-700/層'],
      notes: '宜蘭↔台北跨縣市搬家約 +3,000-4,000元。山區民宿搬遷需提前規劃。',
      popular: false
    },
    {
      region: '苗栗縣',
      city: '苗栗市、頭份市、竹南鎮',
      difficulty: '中',
      prices: {
        studio: '2,800-4,200',
        twoRoom: '5,200-7,500',
        threeRoom: '7,500-12,000',
        office: '12,000-23,000'
      },
      extraFees: ['山區加價 +1,500-3,000', '跨縣市費用', '樓層費 +300-700/層'],
      notes: '山區較多，搬家需注意道路狀況。跨縣市搬家建議提前預約。',
      popular: false
    },
    {
      region: '彰化縣',
      city: '彰化市、員林市、和美鎮',
      difficulty: '低',
      prices: {
        studio: '2,500-3,800',
        twoRoom: '4,800-7,000',
        threeRoom: '7,000-11,000',
        office: '11,000-21,000'
      },
      extraFees: ['工業區 +1,000-2,000', '跨縣市費用', '樓層費 +300-600/層'],
      notes: '平原地形，搬家便利。工業區搬遷需配合廠區管制時間。',
      popular: false
    },
    {
      region: '南投縣',
      city: '南投市、草屯鎮、埔里鎮',
      difficulty: '高',
      prices: {
        studio: '3,500-5,000',
        twoRoom: '6,000-9,000',
        threeRoom: '9,000-14,000',
        office: '14,000-26,000'
      },
      extraFees: ['山區加價 +2,000-5,000', '民宿搬遷另計', '樓層費 +400-800/層'],
      notes: '山區地形複雜，搬家難度高。日月潭、清境民宿搬遷需專業規劃。',
      popular: false
    },
    {
      region: '嘉義市縣',
      city: '嘉義市、太保市、民雄鄉',
      difficulty: '低',
      prices: {
        studio: '2,500-3,800',
        twoRoom: '4,800-7,000',
        threeRoom: '7,000-11,000',
        office: '11,000-21,000'
      },
      extraFees: ['山區加價 +1,000-2,000', '跨縣市費用', '樓層費 +300-600/層'],
      notes: '市區搬家便利，價格實惠。阿里山等山區需加收費用。',
      popular: false
    },
    {
      region: '屏東縣',
      city: '屏東市、潮州鎮、東港鎮',
      difficulty: '中',
      prices: {
        studio: '2,800-4,200',
        twoRoom: '5,200-7,500',
        threeRoom: '7,500-12,000',
        office: '12,000-23,000'
      },
      extraFees: ['墾丁地區 +2,000-4,000', '漁港搬運 +1,000-2,000', '樓層費 +300-700/層'],
      notes: '墾丁民宿搬遷需提前預約。恆春半島道路狹窄，需小型車輛。',
      popular: false
    },
    {
      region: '花蓮縣',
      city: '花蓮市、吉安鄉、壽豐鄉',
      difficulty: '高',
      prices: {
        studio: '3,800-5,500',
        twoRoom: '6,500-9,500',
        threeRoom: '9,500-15,000',
        office: '15,000-28,000'
      },
      extraFees: ['蘇花公路 +3,000-6,000', '民宿搬遷另計', '樓層費 +400-800/層'],
      notes: '東部搬家費用較高。花蓮↔台北需經蘇花公路，時間長、風險高。',
      popular: false
    },
    {
      region: '台東縣',
      city: '台東市、卑南鄉、成功鎮',
      difficulty: '高',
      prices: {
        studio: '4,000-6,000',
        twoRoom: '7,000-10,000',
        threeRoom: '10,000-16,000',
        office: '16,000-30,000'
      },
      extraFees: ['離島搬家另計', '長途運輸 +5,000-10,000', '樓層費 +400-800/層'],
      notes: '東部最南端，搬家費用最高。綠島、蘭嶼離島搬家需船運，費用另計。',
      popular: false
    }
  ];

  const sortedPrices = [...regionPrices].sort((a, b) => {
    if (sortBy === 'region') {
      return a.region.localeCompare(b.region, 'zh-TW');
    } else {
      const priceA = parseInt(a.prices[selectedType].split('-')[0].replace(/,/g, ''));
      const priceB = parseInt(b.prices[selectedType].split('-')[0].replace(/,/g, ''));
      return priceA - priceB;
    }
  });

  const getTypeLabel = (type: string) => {
    const labels = {
      studio: '套房/雅房',
      twoRoom: '2房',
      threeRoom: '3房以上',
      office: '辦公室/店面'
    };
    return labels[type as keyof typeof labels];
  };

  const getDifficultyColor = (difficulty: string) => {
    const colors = {
      '低': 'text-teal-600 bg-teal-50',
      '中低': 'text-emerald-600 bg-emerald-50',
      '中': 'text-amber-600 bg-amber-50',
      '中高': 'text-orange-600 bg-orange-50',
      '高': 'text-rose-600 bg-rose-50'
    };
    return colors[difficulty as keyof typeof colors] || 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SchemaOrg 
        type="LocalBusiness"
        data={{
          name: '建鈞精緻搬家',
          description: '提供全台16縣市搬家服務，透明價格、專業團隊、4.8分高評價。套房搬家2500元起，免費估價專線：0978-900-081',
          address: {
            streetAddress: '台灣',
            addressLocality: '全台服務',
            addressRegion: '台灣',
            postalCode: '',
            addressCountry: 'TW'
          },
          telephone: '0978-900-081',
          priceRange: '$$',
          areaServed: ['台北市', '新北市', '桃園市', '新竹市', '台中市', '台南市', '高雄市', '基隆市', '宜蘭縣', '苗栗縣', '彰化縣', '南投縣', '嘉義市縣', '屏東縣', '花蓮縣', '台東縣'],
          openingHours: 'Mo-Su 00:00-24:00'
        }}
      />
      
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-600 via-teal-500 to-emerald-500 text-white py-20">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <i className="ri-price-tag-3-line text-xl"></i>
                <span className="text-sm font-medium">2025年最新價格</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                全台搬家價格比較表
              </h1>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                一次看懂16縣市搬家費用差異｜透明價格｜專業分析｜免費估價
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a 
                  href="tel:0978900081"
                  className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-phone-line text-xl"></i>
                  立即來電估價
                </a>
                <a 
                  href="#comparison-table"
                  className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap"
                >
                  <i className="ri-arrow-down-line text-xl"></i>
                  查看價格表
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              <div className="bg-white p-6 rounded-xl text-center shadow-sm">
                <div className="text-3xl font-bold text-teal-600 mb-2">16</div>
                <div className="text-sm text-gray-600">服務縣市</div>
              </div>
              <div className="bg-white p-6 rounded-xl text-center shadow-sm">
                <div className="text-3xl font-bold text-teal-600 mb-2">2,500</div>
                <div className="text-sm text-gray-600">最低價格起（元）</div>
              </div>
              <div className="bg-white p-6 rounded-xl text-center shadow-sm">
                <div className="text-3xl font-bold text-teal-600 mb-2">4.8</div>
                <div className="text-sm text-gray-600">Google評分</div>
              </div>
              <div className="bg-white p-6 rounded-xl text-center shadow-sm">
                <div className="text-3xl font-bold text-teal-600 mb-2">24H</div>
                <div className="text-sm text-gray-600">全天候服務</div>
              </div>
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section id="comparison-table" className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="bg-gray-50 p-6 rounded-2xl mb-8">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Type Filter */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      <i className="ri-home-4-line mr-2"></i>
                      選擇搬家類型
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {(['studio', 'twoRoom', 'threeRoom', 'office'] as const).map((type) => (
                        <button
                          key={type}
                          onClick={() => setSelectedType(type)}
                          className={`px-4 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                            selectedType === type
                              ? 'bg-teal-600 text-white shadow-lg'
                              : 'bg-white text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {getTypeLabel(type)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Sort Filter */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      <i className="ri-sort-desc mr-2"></i>
                      排序方式
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setSortBy('region')}
                        className={`px-4 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                          sortBy === 'region'
                            ? 'bg-teal-600 text-white shadow-lg'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        依地區排序
                      </button>
                      <button
                        onClick={() => setSortBy('price')}
                        className={`px-4 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                          sortBy === 'price'
                            ? 'bg-teal-600 text-white shadow-lg'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        依價格排序
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Price Comparison Table */}
              <div className="space-y-4">
                {sortedPrices.map((item, index) => (
                  <div 
                    key={item.region}
                    className={`bg-white border-2 rounded-2xl p-6 hover:shadow-lg transition-all ${
                      item.popular ? 'border-teal-500' : 'border-gray-200'
                    }`}
                  >
                    {item.popular && (
                      <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
                        <i className="ri-fire-line"></i>
                        熱門地區
                      </div>
                    )}
                    
                    <div className="grid md:grid-cols-12 gap-6 items-start">
                      {/* Region Info */}
                      <div className="md:col-span-3">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{item.region}</h3>
                        <p className="text-sm text-gray-600 mb-3">{item.city}</p>
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(item.difficulty)}`}>
                          <i className="ri-bar-chart-line"></i>
                          難度：{item.difficulty}
                        </span>
                      </div>

                      {/* Price Info */}
                      <div className="md:col-span-5">
                        <div className="bg-gradient-to-br from-teal-50 to-emerald-50 p-5 rounded-xl">
                          <div className="flex items-baseline gap-2 mb-2">
                            <span className="text-3xl font-bold text-teal-600">
                              ${item.prices[selectedType].split('-')[0]}
                            </span>
                            <span className="text-xl text-gray-600">-</span>
                            <span className="text-3xl font-bold text-teal-600">
                              ${item.prices[selectedType].split('-')[1]}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">
                            {getTypeLabel(selectedType)} 搬家費用
                          </p>
                        </div>

                        <div className="mt-4 space-y-2">
                          <p className="text-sm font-semibold text-gray-700">
                            <i className="ri-add-circle-line text-amber-600 mr-1"></i>
                            額外費用：
                          </p>
                          <ul className="space-y-1">
                            {item.extraFees.map((fee, idx) => (
                              <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                                <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                                <span>{fee}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Notes */}
                      <div className="md:col-span-4">
                        <div className="bg-amber-50 p-4 rounded-xl">
                          <p className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
                            <i className="ri-lightbulb-line"></i>
                            搬家建議
                          </p>
                          <p className="text-sm text-amber-800 leading-relaxed">
                            {item.notes}
                          </p>
                        </div>
                        <Link
                          to={`/area/${item.region.replace('市', '').replace('縣', '').toLowerCase()}`}
                          className="mt-3 inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm whitespace-nowrap"
                        >
                          查看{item.region}詳細資訊
                          <i className="ri-arrow-right-line"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Price Factors */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  影響搬家價格的6大因素
                </h2>
                <p className="text-lg text-gray-600">
                  了解這些因素，幫助您更準確估算搬家費用
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  {
                    icon: 'ri-map-pin-line',
                    title: '地區位置',
                    desc: '市中心、偏遠地區、山區、離島等不同位置，價格差異可達50-100%',
                    color: 'teal'
                  },
                  {
                    icon: 'ri-building-line',
                    title: '樓層高度',
                    desc: '無電梯老公寓每層加收300-1,000元，5樓以上費用明顯增加',
                    color: 'emerald'
                  },
                  {
                    icon: 'ri-truck-line',
                    title: '物品數量',
                    desc: '套房、2房、3房物品量差異大，決定車輛大小和人力需求',
                    color: 'cyan'
                  },
                  {
                    icon: 'ri-route-line',
                    title: '搬運距離',
                    desc: '同區、跨區、跨縣市距離不同，運輸成本和時間成本差異大',
                    color: 'sky'
                  },
                  {
                    icon: 'ri-calendar-line',
                    title: '搬家時間',
                    desc: '假日、月初月底、學生畢業季等旺季，價格可能上漲20-30%',
                    color: 'indigo'
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: '特殊需求',
                    desc: '鋼琴搬運、精密設備、古董藝品等特殊物品需額外收費',
                    color: 'violet'
                  }
                ].map((factor, index) => (
                  <div key={index} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all">
                    <div className={`w-14 h-14 bg-${factor.color}-100 rounded-xl flex items-center justify-center mb-4`}>
                      <i className={`${factor.icon} text-2xl text-${factor.color}-600`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{factor.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{factor.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Money Saving Tips */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  省錢搬家5大秘訣
                </h2>
                <p className="text-lg text-gray-600">
                  掌握這些技巧，最多可省下30%搬家費用
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    number: '01',
                    title: '避開搬家旺季',
                    tips: ['避開月初月底（租約到期高峰）', '避開假日和國定假日', '避開學生畢業季（6-7月）', '選擇平日搬家可省20-30%'],
                    icon: 'ri-calendar-check-line'
                  },
                  {
                    number: '02',
                    title: '提前預約規劃',
                    tips: ['提前2週以上預約享早鳥優惠', '避免臨時加價和找不到車', '有充足時間比較多家報價', '可選擇較優惠的時段'],
                    icon: 'ri-time-line'
                  },
                  {
                    number: '03',
                    title: '減少物品數量',
                    tips: ['搬家前先斷捨離不需要的物品', '二手物品可捐贈或販售', '減少物品可降低車輛和人力成本', '打包更輕鬆、搬運更快速'],
                    icon: 'ri-delete-bin-line'
                  },
                  {
                    number: '04',
                    title: '自行打包整理',
                    tips: ['自己打包可省下打包服務費', '提前整理好可縮短搬家時間', '貴重物品自己打包更安心', '紙箱可向超商或賣場索取'],
                    icon: 'ri-box-3-line'
                  },
                  {
                    number: '05',
                    title: '比較多家報價',
                    tips: ['至少比較3家以上搬家公司', '注意報價是否包含所有費用', '確認是否有隱藏費用', '選擇評價好、價格合理的公司'],
                    icon: 'ri-price-tag-3-line'
                  }
                ].map((tip, index) => (
                  <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border-2 border-gray-200 hover:border-teal-500 transition-all">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-16 h-16 bg-teal-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold flex-shrink-0">
                        {tip.number}
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-2xl font-bold text-gray-900 mb-1">{tip.title}</h3>
                        <div className="w-16 h-1 bg-teal-600 rounded-full"></div>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {tip.tips.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-gray-700">
                          <i className={`${tip.icon} text-teal-600 mt-1 flex-shrink-0`}></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  搬家價格常見問題
                </h2>
                <p className="text-lg text-gray-600">
                  最多人詢問的搬家費用問題
                </p>
              </div>

              <div className="space-y-4">
                {[
                  {
                    q: '為什麼不同地區的搬家價格差這麼多？',
                    a: '主要受到以下因素影響：(1) 地區生活成本：台北、新北等都會區人力和營運成本較高；(2) 交通便利性：偏遠地區、山區、離島運輸成本高；(3) 市場競爭：競爭激烈的地區價格較合理；(4) 搬家難度：老舊社區、山坡地形增加搬運難度。一般來說，都會區價格較高但服務選擇多，鄉鎮地區價格較低但需注意額外費用。'
                  },
                  {
                    q: '報價單上的價格是最終價格嗎？還會有額外費用嗎？',
                    a: '正規搬家公司的報價應該包含基本搬運費用，但以下情況可能產生額外費用：(1) 樓層費：無電梯老公寓每層300-1,000元；(2) 距離費：車輛無法停靠需人力搬運超過20公尺；(3) 特殊物品：鋼琴、保險箱、大型家具等；(4) 臨時增加物品或服務。建議在估價時詳細說明所有物品和現場狀況，要求提供完整報價單，避免事後爭議。'
                  },
                  {
                    q: '跨縣市搬家費用怎麼計算？會很貴嗎？',
                    a: '跨縣市搬家費用通常包含：(1) 基本搬運費：依物品數量計算；(2) 運輸費：依距離計算，通常每公里10-20元；(3) 過路費：高速公路通行費；(4) 人力成本：長途搬家耗時較長。例如：台北到台中約150公里，運輸費約2,000-3,000元；台北到高雄約350公里，運輸費約5,000-7,000元。建議選擇有跨縣市搬家經驗的公司，確保物品安全。'
                  },
                  {
                    q: '學生搬家、套房搬家有比較便宜的方案嗎？',
                    a: '有的！許多搬家公司提供學生優惠方案：(1) 套房搬家專案：2,500-4,000元起，適合物品較少的單身族；(2) 學生優惠：出示學生證可享9折優惠；(3) 小型搬家：提供小貨車服務，費用較低；(4) 平日優惠：選擇平日搬家可再省10-20%。建議提前整理好物品、自行打包，可進一步降低費用。我們提供學生專屬優惠，歡迎來電詢問！'
                  },
                  {
                    q: '什麼時候搬家最便宜？什麼時候最貴？',
                    a: '最便宜時段：(1) 平日（週一到週五）比假日便宜20-30%；(2) 淡季（3-5月、9-11月）價格較優惠；(3) 月中（避開月初月底租約高峰）；(4) 非學生畢業季（避開6-7月）。最貴時段：(1) 假日和國定假日；(2) 月初月底（租約到期高峰）；(3) 學生畢業季（6-7月）；(4) 農曆年前（大掃除搬家潮）。建議提前2週預約，選擇平日搬家，可享最優惠價格！'
                  },
                  {
                    q: '老公寓沒電梯，樓層費怎麼算？會很貴嗎？',
                    a: '老公寓樓層費計算方式：(1) 一般標準：每層300-800元（依地區和公司而異）；(2) 台北市區：每層500-1,000元（人力成本較高）；(3) 大型家具：冰箱、洗衣機、沙發等可能加收；(4) 狹窄樓梯：需拆裝或吊運另計費用。例如：4樓無電梯，樓層費約1,500-3,200元。建議：(1) 提前告知樓層狀況；(2) 減少大型家具數量；(3) 選擇經驗豐富的搬家公司，確保安全。'
                  },
                  {
                    q: '如何確保報價透明，避免被亂加價？',
                    a: '保護自己的5個方法：(1) 要求現場估價：讓搬家公司到府評估，提供詳細報價單；(2) 書面報價：要求提供正式報價單，載明所有費用項目；(3) 確認包含項目：基本搬運費、樓層費、距離費、特殊物品等；(4) 詢問額外費用：哪些情況會產生額外費用；(5) 簽訂合約：正式合約保障雙方權益。選擇評價良好、經營多年的搬家公司，避免一頁式網站或價格過低的不明業者。'
                  },
                  {
                    q: '搬家費用可以刷卡或分期付款嗎？',
                    a: '付款方式依各家搬家公司而異：(1) 現金：最常見的付款方式，部分公司提供現金優惠；(2) 轉帳匯款：需提前預約並支付訂金；(3) 刷卡：部分公司提供刷卡服務（可能加收手續費）；(4) 分期付款：少數公司提供分期方案。建議：(1) 預約時詢問付款方式；(2) 確認是否需支付訂金；(3) 保留付款證明和收據；(4) 避免全額預付，建議搬家完成後再付款。我們提供多元付款方式，歡迎來電詢問！'
                  }
                ].map((faq, index) => (
                  <details key={index} className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all group">
                    <summary className="px-6 py-5 cursor-pointer list-none flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4 flex items-start gap-3">
                        <i className="ri-question-line text-teal-600 mt-1 flex-shrink-0"></i>
                        <span>{faq.q}</span>
                      </h3>
                      <i className="ri-arrow-down-s-line text-2xl text-gray-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                    </summary>
                    <div className="px-6 pb-5 pt-2">
                      <div className="pl-8 text-gray-600 leading-relaxed border-l-4 border-teal-500 ml-3">
                        {faq.a}
                      </div>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-teal-600 via-teal-500 to-emerald-500 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                需要更精確的搬家報價？
              </h2>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                每個搬家案件都是獨特的，我們提供免費到府估價服務<br />
                專業團隊為您量身規劃，給您最透明、最合理的價格
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a 
                  href="tel:0978900081"
                  className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-phone-line text-xl"></i>
                  0978-900-081
                </a>
                <Link
                  to="/quote"
                  className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap"
                >
                  <i className="ri-file-list-3-line text-xl"></i>
                  線上填寫估價單
                </Link>
              </div>
              <p className="mt-6 text-white/80 text-sm">
                ⭐ 4.8分高評價 ｜ 156則真實好評 ｜ 透明收費無隱藏費用
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PriceComparison;
